<template>
  <div v-if="invitations.length > 0" data-test="hire-invitations-list">
    <BaseNoticeItem
      v-for="invitation in invitations"
      :key="invitation.hireId"
      info
      :button-text="invitation.requiredCustomFieldsComplete ? $t('Review details') : $t('Add details')"
      :button-url="laravelNamedRoute('hires.hire.view', { hire: invitation.number, tab: 'overview' })"
    >
      <template #head>
        <strong>{{ heading(invitation) }}</strong>
      </template>
      <template #body>
        {{ body(invitation) }}
      </template>
    </BaseNoticeItem>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import BaseNoticeItem from '@/components/BaseNoticeItem.vue'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const emit = defineEmits<{ 'has-invitations': [value: boolean] }>()

const { isRecruiter } = useAccountCapabilities()

const { result } = useQuery(
  graphql(`
    query HireInvitationsList {
      hireInvitations {
        hireId
        number
        jobTitle
        companyName
        workerName
        requiredCustomFieldsComplete
      }
    }
  `),
  {},
  { clientId: 'publicApi' },
)

const invitations = computed(() => result.value?.hireInvitations ?? [])

type Invitation = (typeof invitations.value)[number]

function heading(invitation: Invitation) {
  const company = invitation.companyName ?? ''

  return invitation.requiredCustomFieldsComplete
    ? $t('Details added for :company', { company })
    : $t('Details requested by :company', { company })
}

function body(invitation: Invitation) {
  const company = invitation.companyName ?? ''
  const worker = invitation.workerName ?? ''
  const job = invitation.jobTitle ? `“${invitation.jobTitle}”` : $t('this job')

  if (invitation.requiredCustomFieldsComplete) {
    return isRecruiter.value
      ? $t(
          ':company has the details for :worker. You can review or update them until an offer is sent.',
          { company, worker },
        )
      : $t(
          ':company has your details. You can review or update them until an offer is sent.',
          { company },
        )
  }

  return isRecruiter.value
    ? $t(':company needs you to add some details for :worker before :job can go ahead.', {
        company,
        worker,
        job,
      })
    : $t(':company needs you to add some details before :job can go ahead.', { company, job })
}

watch(invitations, (list) => emit('has-invitations', list.length > 0), { immediate: true })
</script>
